import type { UIEvent as ReactUIEvent } from "react";
import { action, computed, makeObservable, observable } from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import {
  normalizeSliderPhotos,
  resolveActiveSlideIndex,
} from "./viewmodel/slider-logic";

export interface ProductPhotoSliderProps {
  photo?: string;
  photos?: string[];
  alt: string;
  onActiveIndexChange?: (index: number) => void;
  className?: string;
  trackClassName?: string;
  imageClassName?: string;
  emptyClassName?: string;
  eagerFirstImage?: boolean;
}

@injectable()
export class ProductPhotoSliderViewModel extends ViewModel<
  unknown,
  ProductPhotoSliderProps
> {
  @observable
  activeIndex = 0;

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get photos(): string[] {
    return normalizeSliderPhotos({
      photo: this.viewProps.photo,
      photos: this.viewProps.photos,
    });
  }

  @computed
  get hasPhotos(): boolean {
    return this.photos.length > 0;
  }

  @computed
  get hasManyPhotos(): boolean {
    return this.photos.length > 1;
  }

  @computed
  get activeDotIndex(): number {
    if (!this.hasManyPhotos) {
      return 0;
    }

    return Math.min(Math.max(0, this.activeIndex), this.photos.length - 1);
  }

  @computed
  get shouldUseEagerLoading(): boolean {
    return this.viewProps.eagerFirstImage ?? false;
  }

  @computed
  get alt(): string {
    return this.viewProps.alt;
  }

  @computed
  get className(): string | undefined {
    return this.viewProps.className;
  }

  @computed
  get trackClassName(): string | undefined {
    return this.viewProps.trackClassName;
  }

  @computed
  get imageClassName(): string | undefined {
    return this.viewProps.imageClassName;
  }

  @computed
  get emptyClassName(): string | undefined {
    return this.viewProps.emptyClassName;
  }

  @action.bound
  setTrackNode(node: HTMLDivElement | null): void {
    if (!node) {
      this.setActiveIndex(0);
      return;
    }

    this.syncActiveIndexFromNode(node);
  }

  @action.bound
  onTrackScroll(event: ReactUIEvent<HTMLDivElement>): void {
    this.syncActiveIndexFromNode(event.currentTarget);
  }

  protected onViewUpdated(): void {
    const maxIndex = Math.max(0, this.photos.length - 1);
    if (this.activeIndex > maxIndex) {
      this.setActiveIndex(maxIndex);
    }
  }

  private syncActiveIndexFromNode(node: HTMLDivElement): void {
    this.setActiveIndex(
      resolveActiveSlideIndex({
        scrollLeft: node.scrollLeft,
        viewportWidth: node.clientWidth,
        slidesCount: this.photos.length,
      }),
    );
  }

  private setActiveIndex(index: number): void {
    const maxIndex = Math.max(0, this.photos.length - 1);
    const next = Math.max(0, Math.min(index, maxIndex));
    if (this.activeIndex === next) {
      return;
    }

    this.activeIndex = next;
    this.viewProps.onActiveIndexChange?.(next);
  }

  protected onViewMounted(): void {
    if (this.photos.length === 0) {
      return;
    }

    this.viewProps.onActiveIndexChange?.(this.activeDotIndex);
  }
}
